import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { LuCheck as Check, LuTrash2 as Trash2 } from 'react-icons/lu';
import { GroupMemberRow } from './types';

type Props = {
  members: GroupMemberRow[];
  filteredMembers: GroupMemberRow[];
  loading: boolean;
  isEditMode: boolean;
  canManageMembers: boolean;
  draftOrders: Record<string, string>;
  selectedOrders: string[];
  orderError: string | null;
  onToggleOrder: (member: GroupMemberRow) => void;
  onDraftOrderChange: (memberEmail: string, value: string) => void;
  onRemoveMember: (memberEmail: string) => void;
  onToggleOrderChecked?: (member: GroupMemberRow) => void;
  loadingOrderChecked?: Set<string>;
};

function toNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function normalizeStatus(value: string | undefined, fallback = 'NA'): string {
  if (!value || !value.trim()) return fallback;
  const normalized = value.trim().toLowerCase();

  if (normalized === 'approved') return 'Approved';
  if (normalized === 'confirmed') return 'Confirmed';
  if (normalized === 'pending') return 'Pending';
  if (normalized === 'paid') return 'Paid';
  if (normalized === 'na' || normalized === 'n/a') return 'NA';
  if (normalized === 'rejected') return 'Rejected';

  return value;
}

function StatusBadge({ type, text }: { type: 'success' | 'danger' | 'warning'; text: string }) {
  const colors = {
    success: { borderColor: '#a7f3d0', bg: '#ecfdf5', color: '#047857' },
    danger: { borderColor: '#fecdd3', bg: '#fff1f2', color: '#be123c' },
    warning: { borderColor: '#fde68a', bg: '#fffbeb', color: '#b45309' }
  };

  return (
    <Chip
      label={text}
      variant="outlined"
      sx={{
        height: 32,
        borderRadius: 999,
        borderWidth: 1,
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        ...colors[type],
        '& .MuiChip-label': { px: 1.2 }
      }}
    />
  );
}

export function GroupMembersTable({
  members,
  filteredMembers,
  loading,
  isEditMode,
  canManageMembers,
  draftOrders,
  selectedOrders,
  orderError,
  onToggleOrder,
  onDraftOrderChange,
  onRemoveMember,
  onToggleOrderChecked,
  loadingOrderChecked = new Set()
}: Props) {
  const totalColumns = canManageMembers ? 6 : 5;

  return (
    <>
      {orderError && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={orderError}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              borderRadius: 2,
              px: 1,
              py: 3,
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              fontWeight: 600
            }}
          />
        </Box>
      )}

      <Card
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(226, 232, 240, 1)',
          boxShadow: '0 20px 40px rgba(148, 163, 184, 0.18)'
        }}
      >
        <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: '#fff' }}>
          <Table sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(241, 245, 249, 0.8)' }}>
                {canManageMembers && (
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: 11,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      color: '#64748b'
                    }}
                  >
                    Action
                  </TableCell>
                )}
                <TableCell
                  align="center"
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: '#64748b'
                  }}
                >
                  Order
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: '#64748b'
                  }}
                >
                  Member
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: '#64748b'
                  }}
                >
                  Paid Amount
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: '#64748b'
                  }}
                >
                  Payment Status
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: '#64748b'
                  }}
                >
                  Admin Status
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={totalColumns}>
                    <Box
                      sx={{
                        py: 5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1.5
                      }}
                    >
                      <CircularProgress size={28} />
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                        Loading members...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMembers.map((member) => {
                  const isSelected =
                    selectedOrders.includes(member.memberEmail) || !!member.isApproved;
                  const contributionValue = toNumber(member.pendingContributionAmount);
                 const rawAdminStatus = normalizeStatus(member.adminConfirmed, 'NA');

const adminStatusText =
  rawAdminStatus === 'Pending'
    ? 'Pending'
    : 'NA';

const paymentStatusText =
  rawAdminStatus === 'Pending'
    ? 'Paid'
    : 'NA';

const paymentStatusType: 'success' | 'warning' | 'danger' =
  paymentStatusText === 'Paid'
    ? 'success'
    : 'danger';

const adminStatusType: 'success' | 'warning' | 'danger' =
  adminStatusText === 'Pending'
    ? 'warning'
    : 'danger';

                  return (
                    <TableRow
                      key={member.memberEmail}
                      hover
                      sx={{
                        backgroundColor: isEditMode ? 'rgba(254, 243, 199, 0.4)' : 'transparent'
                      }}
                    >
                      {canManageMembers && (
                        <TableCell align="center" sx={{ position: 'relative' }}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <IconButton
                              onClick={() => onRemoveMember(member.memberEmail)}
                              size="small"
                              sx={{ color: '#ef4444' }}
                            >
                              <Trash2 size={18} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}

                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1
                          }}
                        >
                          <IconButton
                            onClick={() => {
                              if (canManageMembers && onToggleOrderChecked)
                                onToggleOrderChecked(member);
                            }}
                            disabled={
                              !canManageMembers || loadingOrderChecked.has(member.memberEmail)
                            }
                            aria-pressed={member.orderChecked ?? false}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: member.orderChecked ? '#86efac' : '#e2e8f0',
                              backgroundColor: member.orderChecked ? '#f0fdf4' : '#f8fafc',
                              color: member.orderChecked ? '#166534' : '#334155',
                              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                              '&:hover': {
                                backgroundColor: member.orderChecked ? '#dcfce7' : '#f8fafc'
                              },
                              '&.Mui-disabled': {
                                opacity: 1,
                                color: member.orderChecked ? '#166534' : '#334155',
                                backgroundColor: member.orderChecked ? '#f0fdf4' : '#f8fafc',
                                borderColor: member.orderChecked ? '#86efac' : '#e2e8f0'
                              }
                            }}
                          >
                            {member.orderChecked ? <Check size={18} /> : member.order}
                          </IconButton>

                          {isEditMode && canManageMembers && (
                            <TextField
                              type="number"
                              size="small"
                              value={draftOrders[member.memberEmail] ?? ''}
                              onChange={(event) =>
                                onDraftOrderChange(member.memberEmail, event.target.value)
                              }
                              inputProps={{
                                min: 1,
                                max: members.length,
                                'aria-label': `Change order for ${member.memberName}`
                              }}
                              sx={{
                                width: 72,
                                '& .MuiOutlinedInput-root': { borderRadius: 2, fontWeight: 800 }
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                          <Avatar
                            src={member.memberAvatar}
                            alt={member.memberName}
                            sx={{
                              width: 48,
                              height: 48,
                              border: '2px solid #fff',
                              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
                              outline: '1px solid #e2e8f0'
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 800,
                                color: '#020617',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {member.memberName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: '#94a3b8'
                              }}
                            >
                              Member #{member.order}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#020617' }}>
                          ${contributionValue.toFixed(2)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', mt: 0.5, fontWeight: 700, color: '#94a3b8' }}
                        >
                          Pending
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <StatusBadge type={paymentStatusType} text={paymentStatusText} />
                      </TableCell>

                      <TableCell align="center">
                        <StatusBadge type={adminStatusType} text={adminStatusText} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            borderTop: '1px solid #f1f5f9',
            backgroundColor: 'rgba(248, 250, 252, 0.7)',
            px: 3,
            py: 2.5
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>
            Showing 1 to {filteredMembers.length} of {members.length} members
          </Typography>
        </Box>
      </Card>
    </>
  );
}
